<template>
  <div class="sl-item" @click.stop="openQuickEditor" :class="{'in-basket': isInBasket}">
    <div class="status c-hand" @click.stop="capClicked" :title="statusTitle" :class="{'busy': quickEditorOpen}">
      <fa-icon :icon="statusIcon" :spin="isMoving" />
    </div>
    <item-quick-editor v-if="quickEditorOpen" :item="item" @close="closeQuickEditor" @more-details="editItem"/>
    <template v-else>
      <div class="quantity" :class="{'single-qty': item.quantityNeeded === 1}">{{ item.quantityNeeded }}</div>
      <div class="desc">
        <div class="name-notes">
          <div class="name">
            <span>{{ item.name }}</span>
            <span v-if="item.isEventualPurchase" class="status-icon" title="Purchase eventually">&nbsp;<fa-icon :icon="['far', 'calendar-alt']" /></span>
            <span v-if="item.isOneTimeItem" class="status-icon" title="One-time purchase">&nbsp;<fa-icon icon="backspace" /></span>
          </div>
          <div class="notes">
            <span>{{ item.notes }}</span>
            <span v-if="item.quantityNeeded > 1 && item.priceEachCents" class="quantity-note">
              <template v-if="item.notes">&nbsp;&mdash;&nbsp;</template><amt-cents :cents="item.priceEachCents" /> each
            </span>
          </div>
        </div>
        <div class="store-list">
          <span v-for="s in storesList" :key="s.id" class="label label-rounded sl-store-label">{{ s.name }}</span>
        </div>
      </div>
      <div class="price" :class="{'unknown': !item.priceEachCents}">
        <amt-cents :cents="item.totalPriceCents" v-if="item.totalPriceCents"></amt-cents>
        <fa-icon :icon="['far', 'question-circle']" v-else />
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'NeededItemEntry',
  props: {
    item: { type: Object, required: true },
    isInBasket: { type: Boolean, required: true }
  },
  data () {
    return {
      isMoving: false,
      quickEditorOpen: false
    }
  },
  computed: {
    storesList () {
      return _.map(this.item.storeIds, sId => this.getStoreForId(sId))
    },
    statusTitle () {
      return this.isMoving
        ? this.isInBasket
          ? 'Taking out of basket...'
          : 'Putting in basket...'
        : this.quickEditorOpen
          ? 'Cancel quick edit'
          : this.item.isInCart
            ? 'Take out of basket'
            : 'Put in basket'
    },
    statusIcon () {
      return this.isMoving
        ? 'cog'
        : this.quickEditorOpen
          ? 'pen-square'
          : this.item.isInCart
            ? 'check-square'
            : 'square'
    }
  },
  methods: {
    async capClicked () {
      if (this.quickEditorOpen || this.isMoving) {
        return
      }
      this.isMoving = true
      await this.setItemCartStatus({ id: this.item.id, isInCart: !this.isInBasket })
      this.isMoving = false
    },
    openQuickEditor () {
      // Note: allows editing while in-cart
      this.quickEditorOpen = true
    },
    closeQuickEditor () {
      this.quickEditorOpen = false
    },
    editItem () {
      this.closeQuickEditor()
      this.$router.push({ name: 'item-edit', params: { id: `${this.item.id}`, fromRouteName: 'home' } })
    }
  }
}
</script>

<style lang="scss">
  @import '../assets/_vars';

  .sl-item {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;;
    background-color: $light-color;
    color: $darkest-color;
    width: 100%;
    font-size: 1rem;
    border-bottom: 1px solid $dark-color;
    overflow: hidden;

    > * {
      padding: 0.25rem;
    }

    &:first-of-type {
      border-top-right-radius: 8px;
      border-top-left-radius: 8px;
    }

    &:last-of-type {
      border-bottom-right-radius: 8px;
      border-bottom-left-radius: 8px;
      border-bottom: none;
    }

    .status {
      flex: 0 0 3rem;
      text-align: center;
      background-color: darken($light-color, 20%);
      color: lighten($dark-color, 20%);
      display: flex;
      align-items: center;
      justify-content: center;

      &.busy {
        background-color: darken($light-color, 10%);
        color: lighten($dark-color, 10%);
      }
    }

    .quantity {
      font-size: 1.2rem;
      line-height: 1.5rem;
      flex: 0 0 2rem;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;

      &.single-qty {
        opacity: 0.33;
        @media (max-width: $size-xs) {
          display: none;
        }
      }
    }

    .desc {
      flex: 1 1 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;

      & > .name-notes {
        flex: 1 0 auto;
      }
      & > .store-list {
        flex: 0 1 120px;
      }

      @media (max-width: $size-sm) {
        flex-direction: column;
        align-items: start;
        & > .name-notes {
          flex: 1 0 auto;
        }
        & > .store-list {
          flex: 0 1 auto;
          text-align: left;
        }
      }

      .name {
        line-height: 1.4rem;
      }

      .status-icon {
        display: inline-block;
        color: lighten($primary-color, 10%);
      }

      .notes {
        font-size: 0.8rem;
        font-style: italic;
        color: lighten($dark-color, 10%);

        .quantity-note {
          opacity: 0.67;
        }
      }
    }

    .price {
      flex: 0 1 3rem;
      font-size: 0.8rem;
      line-height: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      color: lighten($dark-color, 10%);
      &.unknown {
        color: lighten($dark-color, 30%);
      }
    }

    &.in-basket {
      background-color: darken($light-color, 20%);
      .status {
        background-color: lighten($dark-color, 20%);
        color: $light-color;
      }
    }
  }
</style>
